import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private readonly THEME_KEY = 'app-theme';
    private themeSubject = new BehaviorSubject<Theme>(this.getStoredTheme());

    public theme$ = this.themeSubject.asObservable();

    constructor() {
        this.initializeTheme();
    }

    private getStoredTheme(): Theme {
        try {
            const storedTheme = localStorage.getItem(this.THEME_KEY);
            return storedTheme === 'light' || storedTheme === 'dark' ? storedTheme : 'dark';
        } catch {
            return 'dark';
        }
    }

    private initializeTheme(): void {
        const theme = this.getStoredTheme();
        this.applyTheme(theme);
    }

    private applyTheme(theme: Theme): void {
        document.documentElement.setAttribute('data-theme', theme);
        // Toggle class on body to trigger PrimeNG dark mode repaint
        if (theme === 'dark') {
            document.documentElement.classList.add('dark-mode');
        } else {
            document.documentElement.classList.remove('dark-mode');
        }
    }

    public getCurrentTheme(): Theme {
        return this.themeSubject.value;
    }

    public setTheme(theme: Theme): void {
        try {
            localStorage.setItem(this.THEME_KEY, theme);
        } catch {
            // Keep the in-memory theme functional when storage is unavailable.
        }
        this.applyTheme(theme);
        this.themeSubject.next(theme);
    }

    public toggleTheme(): void {
        const newTheme = this.themeSubject.value === 'light' ? 'dark' : 'light';
        this.setTheme(newTheme);
    }

    public isDarkMode(): boolean {
        return this.themeSubject.value === 'dark';
    }
}